import { isAuth } from "../../src/pages/auth/ProtectedRoute";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import axios from "axios";

interface ChallengeContextProps {
  view: string;
  changeView: (param: string) => void;
  setView: (view: string) => void;
  tab: string;
  setTab: (view: string) => void;
  leaderboardStat: LeaderboardStat | null;
  fetchLeaderboardStat: () => void;
  fetchLeaderboard: () => void;
  useRemainingTime: (end: string | number | undefined | null) => string;
  loading: boolean;
  leaderboardData: Participant[] | undefined;
  leaderboardResponse: LeaderboardResponse | undefined;
  items: Distribution[];
  reward: RewardResponse | null;
  fetchReward: () => void;
}

const AmbassadorChallengeContext = createContext<
  ChallengeContextProps | undefined
>(undefined);

export const AmbassadorChallengeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const baseUrl = process.env.REACT_APP_HOST_API;

  const [view, setView] = useState("overview");

  const changeView = (param: string) => {
    setView(param);
  };

  const [tab, setTab] = useState("leaderboard");

  const [leaderboardStat, setLeaderboardStat] =
    useState<LeaderboardStat | null>(null);

  const fetchLeaderboardStat = async () => {
    try {
      const response = await axios.get(`${baseUrl}ambassador/stats`, {
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      });

      setLeaderboardStat(response.data.data);
    } catch (error) {
      if (error) {
        console.log("Error fetching Leader board Stat:", error);
      }
    }
  };

  const [leaderboardData, setLeaderboardData] = useState<
    Participant[] | undefined
  >();
  const [leaderboardResponse, setLeaderboardResponse] = useState<
    LeaderboardResponse | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_HOST_API}ambassador/leaderboard`,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      );
      setLeaderboardData(res.data.data);
      setLeaderboardResponse(res.data.data);
    } catch (err: any) {
      console.log("error fetching leaderboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const useRemainingTime = (
    end: string | number | undefined | null
  ): string => {
    const calculateRemaining = (): string => {
      if (!end) return "";

      const endDate = new Date(
        typeof end === "string" ? end.replace(" ", "T") : end
      ).getTime();

      const now = Date.now();
      let diff = endDate - now;

      if (isNaN(endDate) || diff <= 0) return "0d 0h 0m 0s";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      diff %= 1000 * 60 * 60 * 24;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      diff %= 1000 * 60 * 60;

      const minutes = Math.floor(diff / (1000 * 60));
      diff %= 1000 * 60;

      const seconds = Math.floor(diff / 1000);

      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    const [remaining, setRemaining] = useState<string>(calculateRemaining());

    useEffect(() => {
      // Update every second now that seconds are included
      const interval = setInterval(() => {
        setRemaining(calculateRemaining());
      }, 1000);

      // Initial immediate calculation
      setRemaining(calculateRemaining());

      return () => clearInterval(interval);
    }, [end]);

    return remaining;
  };

  const [items, setItems] = useState<Distribution[]>([]);

  const [reward, setReward] = useState<RewardResponse | null>(null);

  const fetchReward = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_HOST_API}ambassador/reward-distribution`,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      );
      setReward(res.data);
      setItems(res.data.data.distributions);
    } catch (err: any) {
      console.log("error fetching reward data:", err);
    }
  };

  const contextValue = {
    view,
    items,
    reward,
    fetchReward,
    changeView,
    setView,
    setTab,
    tab,
    leaderboardStat,
    fetchLeaderboardStat,
    loading,
    leaderboardData,
    leaderboardResponse,
    fetchLeaderboard,
    useRemainingTime,
  };

  return (
    <AmbassadorChallengeContext.Provider value={contextValue}>
      {children}
    </AmbassadorChallengeContext.Provider>
  );
};

export const useAmbassadorChallengeContext = () => {
  const context = useContext(AmbassadorChallengeContext);
  if (!context) {
    throw new Error(
      "useAmbassadorChallengeContext must be used within an AmbassadorChallengeProvider"
    );
  }
  return context;
};
